'use client';

import { useMemo, useState } from 'react';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { DateTime } from 'luxon';
import { usePathname, useSearchParams } from 'next/navigation';

import Button from '@/components/ui/button/button';
import { useUser } from '@/providers/UserProvider';
import { useTenantConfig } from '@/providers/ConfigProvider';
import useGetMobileAppWebViewHeader from '@/hooks/use-get-mobile-app-web-view';
import { useProfileSlider } from '@/providers/ProfileSliderProvider';
import {
    createLpoCheckout,
    LpoCheckoutError,
} from '@/helpers/api/lpo-checkout-api';
import {
    isPurchasable,
    MAX_QUANTITY,
    validateQuantity,
} from '@/lib/stripe/price';

export interface BuyNowOffer {
    sys: { id: string };
    slug: string;
    name: string;
    salesPrice?: string | null;
    offerValidTill?: string | null;
}

export interface BuyNowLabels {
    buyNow?: string;
    processing?: string;
    loginToBuy?: string;
    quantity?: string;
    error?: string;
}

interface BuyNowProps {
    offer: BuyNowOffer;
    nodeLocale: string;
    parentSlug: string;
    stripeEnabled: boolean;
    mpId?: string;
    labels?: BuyNowLabels;
}

const DEFAULT_LABELS: Required<BuyNowLabels> = {
    buyNow: 'Buy now',
    processing: 'Processing…',
    loginToBuy: 'Log in to buy',
    quantity: 'Quantity',
    error: 'Something went wrong. Please try again.',
};

export const BuyNow = ({
    offer,
    nodeLocale,
    parentSlug,
    stripeEnabled,
    mpId,
    labels,
}: BuyNowProps) => {
    const { isLoggedIn } = useUser();
    const config = useTenantConfig();
    const { openSlider } = useProfileSlider();
    const isWebView = useGetMobileAppWebViewHeader();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [quantity, setQuantity] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const isMobile = useIsMobile();


    const text = { ...DEFAULT_LABELS, ...labels };
    const purchasable = useMemo(
        () => isPurchasable(offer, DateTime.now()),
        [offer],
    );

    if (!stripeEnabled || !purchasable) return null;

    const decreaseQuantity = () =>
        setQuantity((current) => Math.max(1, current - 1));
    const increaseQuantity = () =>
        setQuantity((current) => Math.min(MAX_QUANTITY, current + 1));

    const getCurrentPath = (): string => {
        const params = searchParams.toString();
        return params ? `${pathname}?${params}` : (pathname ?? '/');
    };

    const requestLogin = () => openSlider(getCurrentPath());

    const handleBuy = async () => {
        if (loading) return;
        if (!isLoggedIn) {
            requestLogin();
            return;
        }
        if (!validateQuantity(quantity)) return;
        setLoading(true);
        setHasError(false);
        try {
            const { url } = await createLpoCheckout({
                offerId: offer.sys.id,
                quantity,
                locale: nodeLocale,
                parentSlug,
                prefix: config?.GATSBY_SITE_PATH_PREFIX || '',
                webView: isWebView,
                mpid: mpId,
            });
            window.location.href = url;
        } catch (error) {
            // Session expired mid-flow → prompt login again.
            if (error instanceof LpoCheckoutError && error.status === 401) {
                setLoading(false);
                requestLogin();
                return;
            }
            setHasError(true);
            setLoading(false);
        }
    };

    return (
        <div className="mt-6 mb-2">
            <div className={`flex flex-wrap items-center ${isMobile ? 'justify-between' : 'gap-4'}`}>
                <div
                    role="group"
                    aria-label={text.quantity}
                    className={`flex items-center gap-1 rounded-full border-2 border-neutral-grey-200 justify-around py-1 px-2 ${isMobile ? 'w-[45%]' : ''}`}
                >
                    <button
                        type="button"
                        aria-label={`${text.quantity}: decrease`}
                        onClick={decreaseQuantity}
                        disabled={loading || quantity <= 1}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-headingm leading-none text-text-default transition-colors lg:hover:bg-background-secondary disabled:text-neutral-grey-300"
                    >
                        −
                    </button>
                    <span
                        aria-live="polite"
                        className="w-8 text-center text-bodyl font-bold text-text-default"
                    >
                        {quantity}
                    </span>
                    <button
                        type="button"
                        aria-label={`${text.quantity}: increase`}
                        onClick={increaseQuantity}
                        disabled={loading || quantity >= MAX_QUANTITY}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-headingm leading-none text-text-default transition-colors lg:hover:bg-background-secondary disabled:text-neutral-grey-300"
                    >
                        +
                    </button>
                </div>
                <Button disabled={loading} onClick={handleBuy} className={`${isMobile ? 'w-[45%]' : 'w-36'}`}>
                    {loading ? text.processing : text.buyNow}
                </Button>
            </div>
            {hasError && (
                <p className="mt-2 text-bodys text-semantic-negative" role="alert">
                    {text.error}
                </p>
            )}
        </div>
    );
};

export default BuyNow;
